<script>
  import Layout from '../components/Layout.svelte';
  import { onMount, onDestroy } from 'svelte';
  import { navigate, useLocation } from 'svelte-routing';
  import { createCurrencyFormatter } from '../lib/currency.js';
  import { user, refreshAwaitingApprovalCount } from '../stores/authStore';
  import { resolveBillDueDate, describeDueTerms } from '../lib/dueTerms.js';

  const location = useLocation();
  let lastPath = '';

  let uploading = false;
  let uploadProgress = new Map();
  let bills = [];
  let selectedBillIds = new Set();
  let error = null;
  let approvalNotice = null;
  let approvalNoticeId = null;
  let pollInterval = null;
  let isDragging = false;
  let reprocessing = false;
  let deleting = false;
  let showDeleteConfirm = false;
  let deleteTargetIds = [];

  // Camera capture only works on devices with a real camera intent (mobile/tablet).
  // On desktop the "Take Photo" button is greyed out.
  let isTouchDevice = false;
  // Android only. iOS must not get `capture`, or Safari drops the
  // Take Photo / Photo Library sheet.
  let isAndroid = false;

  // Tabs
  let activeTab = 'ready';
  let accountingPlatform = 'xero';
  let inOrg = false;
  let seeTeamUploads = false;
  let savingSeeTeamUploads = false;
  $: isQuickBooks = accountingPlatform === 'quickbooks';
  
  // Watch for route changes and refresh when returning to this page
  $: if ($location.pathname === '/direct-bill-import' && lastPath !== '/direct-bill-import') {
    console.log('Returned to DirectBillImport, refreshing bills...');
    fetchBills();
    loadSettings();
    refreshAwaitingApprovalCount();
    const notice = $location.state?.approvalNotice;
    if (notice?.id && notice.id !== approvalNoticeId) {
      approvalNoticeId = notice.id;
      approvalNotice = notice.text;
      setTimeout(() => { approvalNotice = null; }, 4000);
    }
    lastPath = $location.pathname;
  } else if ($location.pathname !== '/direct-bill-import') {
    lastPath = $location.pathname;
  }

  // Fetch uploaded bills from API
  async function fetchBills() {
    try {
      const statuses = 'uploaded,processing,completed,failed,awaiting_approval,synced';
      const response = await fetch(`/api/direct-bills/list?status=${statuses}`);
      const data = await response.json();

      if (response.ok && data.success) {
        bills = (data.uploads || []).sort((a, b) => 
          new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0)
        );
        
        // Check if any bills are still processing
        const hasProcessing = bills.some(b => b.status === 'processing' || b.status === 'uploaded');
        
        if (hasProcessing) {
          const uploadIds = bills
            .filter(b => b.status === 'processing' || b.status === 'uploaded')
            .map(b => b.uploadId);
          
          if (uploadIds.length > 0) {
            await processBatch(uploadIds);
          }
        }
      } else {
        throw new Error(data.error || 'Failed to fetch bills');
      }
    } catch (err) {
      console.error('Error fetching bills:', err);
      error = err.message;
    }
  }

  // Process a batch of uploads to check Textract status
  async function processBatch(uploadIds) {
    try {
      const response = await fetch('/api/direct-bills/process-batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ uploadIds })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update bills with new statuses
        data.results.forEach(result => {
          const billIndex = bills.findIndex(b => b.uploadId === result.uploadId);
          if (billIndex !== -1) {
            bills[billIndex].status = result.status;
            if (result.extractedData) {
              bills[billIndex].extractedData = result.extractedData;
            }
          }
        });
        bills = [...bills]; // Trigger reactivity
      }
    } catch (err) {
      console.error('Error processing batch:', err);
    }
  }

  // Handle file drop
  // PDFs plus the photo types Claude Vision reads (mobile camera capture).
  const ACCEPTED_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
  const isAcceptedFile = (f) => ACCEPTED_MIME.includes(f.type);

  function handleDrop(event) {
    event.preventDefault();
    isDragging = false;

    const files = Array.from(event.dataTransfer.files).filter(isAcceptedFile);

    if (files.length > 0) {
      uploadFiles(files);
    } else {
      error = 'Please drop a PDF or a photo (JPG, PNG, WEBP)';
    }
  }

  // Handle drag events
  function handleDragOver(event) {
    event.preventDefault();
    isDragging = true;
  }

  function handleDragLeave(event) {
    event.preventDefault();
    isDragging = false;
  }

  // Handle file input change
  function handleFileInput(event) {
    const files = Array.from(event.target.files).filter(isAcceptedFile);

    if (files.length > 0) {
      uploadFiles(files);
    } else if (event.target.files.length > 0) {
      error = 'Please choose a PDF or a photo (JPG, PNG, WEBP)';
    }

    // Reset input
    event.target.value = '';
  }

  // Upload files to server
  async function uploadFiles(files) {
    uploading = true;
    error = null;

    // Initialize progress for each file
    files.forEach(file => {
      uploadProgress.set(file.name, { status: 'uploading', progress: 0 });
    });
    uploadProgress = new Map(uploadProgress);

    // Process files in batches of 4
    const BATCH_SIZE = 4;
    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      const batch = files.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(file => uploadFile(file)));
    }

    uploading = false;
    
    // Small delay for DynamoDB consistency, then refresh
    await new Promise(r => setTimeout(r, 1000));
    await fetchBills();
  }

  // Upload a single file
  async function uploadFile(file) {
    const formData = new FormData();
    formData.append('files', file);

    try {
      const response = await fetch('/api/direct-bills/upload', {
        method: 'POST',
        body: formData
      });

      if (response.status === 413) {
        throw new Error('File is too large. Maximum upload size is 10MB.');
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(response.ok ? 'Server returned an unexpected response' : `Upload failed (${response.status}). Please refresh and try again.`);
      }

      const data = await response.json();

      if (response.ok && data.success) {
        const warning = data.uploads?.find(u => u.warning)?.warning || null;
        uploadProgress.set(file.name, { status: 'success', progress: 100, warning });
        // A duplicate warning stays until the next upload; plain successes clear.
        if (!warning) {
          setTimeout(() => {
            uploadProgress.delete(file.name);
            uploadProgress = new Map(uploadProgress);
          }, 2000);
        }
      } else {
        uploadProgress.set(file.name, { status: 'error', progress: 0, error: data.error });
      }
    } catch (err) {
      console.error(`Error uploading ${file.name}:`, err);
      uploadProgress.set(file.name, { status: 'error', progress: 0, error: err.message });
    }
    
    uploadProgress = new Map(uploadProgress);
  }

  // Toggle bill selection
  function toggleBillSelection(uploadId) {
    selectedBillIds = new Set(selectedBillIds);
    if (selectedBillIds.has(uploadId)) {
      selectedBillIds.delete(uploadId);
    } else {
      selectedBillIds.add(uploadId);
    }
  }

  // Bills on the current tab that can be ticked (not still uploading/extracting).
  $: selectableBills = activeBills.filter(b => b.status !== 'processing' && b.status !== 'uploaded');
  $: allSelectableSelected = selectableBills.length > 0 && selectableBills.every(b => selectedBillIds.has(b.uploadId));
  $: someSelectableSelected = selectableBills.some(b => selectedBillIds.has(b.uploadId));

  function toggleSelectAll() {
    selectedBillIds = allSelectableSelected
      ? new Set()
      : new Set(selectableBills.map(b => b.uploadId));
  }

  // Due date to show in the list: the bill's own, or the default terms from
  // Settings when the bill has none (shown greyed, applied on review).
  let directBillDueTerms = null;
  // Terms and tab are passed in from the template so Svelte re-renders when they change.
  function listDueDate(bill, terms, tab) {
    const data = bill.extractedData || {};
    if (tab === 'synced' || !terms) return { dueDate: data.dueDate, defaulted: false };
    return resolveBillDueDate({ issueDate: data.issueDate, dueDate: data.dueDate }, terms);
  }

  // Process selected bills
  function processSelected() {
    const selectedBills = bills.filter(b => selectedBillIds.has(b.uploadId));
    
    if (selectedBills.length === 0) {
      return;
    }

    // Navigate to review page with selected bills
    navigate('/review-direct-bills', { state: { selectedBills } });
  }

  function isOverheadXeroOnly(bill) {
    return !!(bill?.extractedData?.accountingOnly && bill.syncedToXero && !bill.syncedToJT);
  }

  function isEmailedToOwner(bill) {
    return bill?.source === 'email' && $user?.id != null && String(bill.userId) !== String($user.id);
  }

  function uploaderLabel(bill) {
    if (!bill?.uploaderName) return null;
    if ($user?.id != null && String(bill.userId) === String($user.id)) return null;
    return bill.uploaderName;
  }

  async function toggleSeeTeamUploads(next) {
    const previous = seeTeamUploads;
    if (previous === next) return;
    seeTeamUploads = next;
    savingSeeTeamUploads = true;
    error = null;
    try {
      const response = await fetch('/api/users/see-team-uploads', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seeTeamUploads: next })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save team uploads setting');
      }
      await fetchBills();
    } catch (err) {
      seeTeamUploads = previous;
      error = err.message || 'Failed to save team uploads setting.';
    } finally {
      savingSeeTeamUploads = false;
    }
  }

  function isDirectBillFullySynced(bill) {
    if (!bill) return false;
    if (isQuickBooks) return !!bill.syncedToJT;
    if (bill.status === 'synced') return true;
    if (bill.syncedToXero && bill.syncedToJT) return true;
    return isOverheadXeroOnly(bill);
  }

  // Open a single bill in review view
  function openBill(bill) {
    if (bill.status === 'processing' || bill.status === 'uploaded' || bill.status === 'failed') return;
    navigate('/review-direct-bills', { state: { selectedBills: [bill], readOnly: isDirectBillFullySynced(bill) } });
  }

  // Reprocess selected bills
  async function reprocessSelected() {
    const uploadIds = Array.from(selectedBillIds);
    
    if (uploadIds.length === 0) {
      return;
    }

    reprocessing = true;
    error = null;

    try {
      const response = await fetch('/api/direct-bills/reprocess', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ uploadIds })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update bill statuses
        uploadIds.forEach(uploadId => {
          const billIndex = bills.findIndex(b => b.uploadId === uploadId);
          if (billIndex !== -1) {
            bills[billIndex].status = 'processing';
            bills[billIndex].extractedData = null;
          }
        });
        bills = [...bills]; // Trigger reactivity
        
        // Clear selection
        selectedBillIds = new Set();
        
        // Start polling for updates
        await fetchBills();
      } else {
        throw new Error(data.error || 'Failed to reprocess bills');
      }
    } catch (err) {
      console.error('Error reprocessing bills:', err);
      error = err.message;
    } finally {
      reprocessing = false;
    }
  }

  // Prompt delete confirmation for a single bill
  function confirmDeleteBill(uploadId) {
    deleteTargetIds = [uploadId];
    showDeleteConfirm = true;
  }

  // Prompt delete confirmation for selected bills
  function confirmDeleteSelected() {
    const ids = Array.from(selectedBillIds).filter(id => {
      const bill = bills.find(b => b.uploadId === id);
      return bill && bill.status !== 'synced';
    });
    if (ids.length === 0) return;
    deleteTargetIds = ids;
    showDeleteConfirm = true;
  }

  // Execute deletion after confirmation
  async function executeDelete() {
    deleting = true;
    error = null;
    showDeleteConfirm = false;

    const failed = [];
    for (const uploadId of deleteTargetIds) {
      try {
        const response = await fetch(`/api/direct-bills/${uploadId}`, { method: 'DELETE' });
        const data = await response.json();
        if (!response.ok || !data.success) {
          failed.push(data.error || 'Delete failed');
        }
      } catch (err) {
        failed.push(err.message);
      }
    }

    if (failed.length > 0) {
      error = `Failed to delete ${failed.length} bill(s): ${failed.join(', ')}`;
    }

    deleteTargetIds = [];
    selectedBillIds = new Set();
    deleting = false;
    await fetchBills();
  }

  // Format date
  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  }

  $: _currencyFmt = createCurrencyFormatter($user?.country);
  $: formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return _currencyFmt(amount);
  };

  function getStatusClass(bill) {
    if (isDirectBillFullySynced(bill)) return 'bg-green-100 text-green-800';
    if (!isQuickBooks && (bill.syncedToXero || bill.syncedToJT)) return 'bg-yellow-100 text-yellow-800';
    switch (bill.status) {
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'awaiting_approval': return 'bg-orange-100 text-orange-800';
      case 'processing':
      case 'uploaded': return 'bg-gray-100 text-gray-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  function getStatusText(bill) {
    if (isQuickBooks) {
      if (bill.syncedToJT) return 'Sync Complete';
    } else {
      if (bill.syncedToXero && bill.syncedToJT) return 'Sync Complete';
      if (isOverheadXeroOnly(bill)) return 'Xero only (overhead)';
      if (bill.syncedToXero) return 'Xero Only';
      if (bill.syncedToJT) return 'JT Only';
    }
    switch (bill.status) {
      case 'uploaded': return 'Uploaded';
      case 'processing': return 'Processing...';
      case 'completed': return 'Processed - Ready to Check';
      case 'awaiting_approval': return 'Checked - Awaiting Approval';
      case 'synced': return 'Sync Complete';
      case 'failed': return 'Failed';
      default: return bill.status;
    }
  }

  // Handle page visibility changes to refresh bills list
  function handleVisibilityChange() {
    if (!document.hidden) {
      // Page became visible again, refresh the bill list
      console.log('Page visible, refreshing bills...');
      fetchBills();
    }
  }

  async function loadSettings() {
    try {
      const response = await fetch('/api/user');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          directBillDueTerms = data.user.billSettings?.directBillDueTerms || null;
          accountingPlatform = data.user.accountingPlatform === 'quickbooks' ? 'quickbooks' : 'xero';
          inOrg = !!data.user.orgId;
          seeTeamUploads = data.user.seeTeamUploads === true;
        }
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  }

  onMount(() => {
    // Coarse pointer = touch device (phone/tablet) where the camera intent works.
    isTouchDevice = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
    isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

    fetchBills();
    loadSettings();
    
    // Poll for updates every 5 seconds
    pollInterval = setInterval(() => {
      const hasProcessing = bills.some(b => b.status === 'processing' || b.status === 'uploaded');
      if (hasProcessing) {
        fetchBills();
      }
    }, 5000);
    
    // Listen for visibility changes (when user returns from review page)
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });

  onDestroy(() => {
    if (pollInterval) {
      clearInterval(pollInterval);
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  });

  // Tab-filtered bill lists — use sync flags as the truth for "synced" since status update can lag
  $: syncedBills = bills.filter(b => isDirectBillFullySynced(b));
  // Waiting bills live on the Approvals page, not in Ready to Check.
  $: readyBills = bills.filter(b => b.status !== 'awaiting_approval' && !syncedBills.includes(b));
  $: activeBills = activeTab === 'ready' ? readyBills : syncedBills;

  $: canProcessSelected = selectedBillIds.size > 0 && 
    bills.filter(b => selectedBillIds.has(b.uploadId)).every(b => b.status === 'completed' || b.status === 'awaiting_approval');
  
  $: canReprocessSelected = selectedBillIds.size > 0 && 
    bills.filter(b => selectedBillIds.has(b.uploadId)).every(b => b.status === 'completed' || b.status === 'failed');
  
  $: canDeleteSelected = selectedBillIds.size > 0 && 
    bills.filter(b => selectedBillIds.has(b.uploadId)).some(b => b.status !== 'synced');
</script>

<Layout activeRoute="direct-bill-import">
  <div class="container mx-auto px-4 py-5 sm:py-8">
    <div class="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
      <h1 class="text-xl sm:text-2xl font-bold">Direct Bill Import</h1>
      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-purple-100 text-purple-800">Preview</span>
    </div>
    {#if approvalNotice}
      <div class="mb-3 p-3 bg-green-100 border border-green-300 text-green-800 rounded">{approvalNotice}</div>
    {/if}
    <p class="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">{isQuickBooks ? 'Import your bills directly in one step to JobTread (syncs to QuickBooks)' : 'Import your bills directly in one step to Xero and JobTread'}
      <span class="hidden sm:inline text-xs text-gray-400">— Available free to all plans while in preview phase</span>
    </p>

    <div class="mb-4 sm:mb-6 p-2.5 sm:p-3 bg-blue-50 border border-blue-200 rounded-md">
      <ol class="text-xs sm:text-sm text-blue-800 list-decimal list-inside space-y-0.5 sm:space-y-1">
        <li><strong>Step 1:</strong> Upload invoices below and wait while they are processed (~2 seconds)</li>
        <li><strong>Step 2:</strong> Select the invoices to import to {isQuickBooks ? 'JobTread' : 'Xero and JobTread'} and tap <strong>"Review &amp; Sync"</strong></li>
      </ol>
      {#if !isQuickBooks}
        <p class="hidden sm:block text-xs text-blue-600 mt-2">Review your Xero Chart of Accounts Mapping to JobTread Cost Codes in Settings to fine tune where your bills end up.</p>
      {/if}
    </div>

    <!-- Upload Area -->
    <div class="mb-5 sm:mb-8">
      <div
        class="border-2 border-dashed rounded-lg p-4 sm:p-12 text-center transition-colors
          {isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white hover:border-gray-400'}"
        on:drop={handleDrop}
        on:dragover={handleDragOver}
        on:dragleave={handleDragLeave}
      >
        <svg
          class="mx-auto h-9 w-9 sm:h-12 sm:w-12 text-gray-400 mb-2 sm:mb-4"
          stroke="currentColor"
          fill="none"
          viewBox="0 0 48 48" 
          aria-hidden="true"
        >
          <path 
            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" 
            stroke-width="2" 
            stroke-linecap="round" 
            stroke-linejoin="round" 
          />
        </svg>
        <div class="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center mb-3">
          <!-- File picker — PDFs or photos from the device -->
          <label for="file-upload" class="cursor-pointer inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-3 min-h-[48px] bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Choose File
          </label>
          <input
            id="file-upload"
            type="file"
            class="sr-only"
            multiple
            accept="application/pdf,image/jpeg,image/png,image/webp"
            on:change={handleFileInput}
          />

          <span class="text-xs font-medium text-gray-400 uppercase">or</span>

          <!-- Photo capture — greyed out on desktop where there's no camera intent.
               iOS: leave `capture` off. Safari then shows its native sheet
               (Take Photo / Photo Library / Choose File). Forcing the camera
               there was less reliable.
               Android: `capture="environment"` is required. Without it,
               Android 13+ Chrome sends accept="image/*" to the system Photo
               Picker, which is gallery-only and never offers the camera.
               Choose File stays the library and PDF fallback. Do not remove
               `capture` from this input. -->
          {#if isTouchDevice}
            <label for="camera-capture" class="cursor-pointer inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-3 min-h-[48px] bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Take Photo
            </label>
            <input
              id="camera-capture"
              type="file"
              class="sr-only"
              accept="image/*"
              capture={isAndroid ? 'environment' : undefined}
              on:change={handleFileInput}
            />
          {:else}
            <div class="relative inline-flex w-full sm:w-auto">
              <div
                class="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-3 min-h-[48px] bg-gray-100 border border-gray-200 text-gray-400 font-medium rounded-lg cursor-not-allowed"
                aria-disabled="true"
                title="Available on mobile"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Take Photo
              </div>
              <span class="absolute left-0 right-0 top-full mt-1 text-center text-xs text-gray-400">Available on mobile</span>
            </div>
          {/if}
        </div>
        <p class="hidden sm:block text-sm text-gray-600 mt-5 mb-1">or drag and drop here</p>
        <p class="text-xs text-gray-500">PDF or photo (JPG, PNG, WEBP) — up to 10MB each, 25 files at once</p>
        {#if isTouchDevice}
          <p class="text-xs text-gray-500 mt-1">
            <span class="text-blue-600 font-medium">Note:</span> If you see a black camera you need to enable permissions in your phone
          </p>
        {/if}
      </div>

      <!-- Upload Progress -->
      {#if uploadProgress.size > 0}
        <div class="mt-4 space-y-2">
          {#each Array.from(uploadProgress.entries()) as [fileName, progress]}
            <div class="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-900">{fileName}</p>
                {#if progress.status === 'uploading'}
                  <div class="mt-1 w-full bg-gray-200 rounded-full h-2">
                    <div 
                      class="bg-blue-600 h-2 rounded-full transition-all" 
                      style="width: {progress.progress}%"
                    ></div>
                  </div>
                {:else if progress.status === 'success'}
                  <p class="text-xs text-green-600">✓ Uploaded successfully</p>
                  {#if progress.warning}
                    <p class="text-xs text-amber-700">⚠ Possible duplicate: {progress.warning}</p>
                  {/if}
                {:else if progress.status === 'error'}
                  <p class="text-xs text-red-600">✗ {progress.error || 'Upload failed'}</p>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>

    <!-- Error Message -->
    {#if error}
      <div class="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
        {error}
      </div>
    {/if}

    {#if inOrg}
      <div class="mb-4 flex items-center justify-between p-4 bg-white rounded-lg shadow border border-gray-200">
        <div class="pr-4">
          <p class="text-sm font-medium text-gray-700">See other team members' uploads</p>
          <p class="text-xs text-gray-500 mt-1">
            When on, this list includes uploads from other people in your organisation, so someone in the office can import bills that were uploaded in the field.
          </p>
        </div>
        <div class="flex items-center gap-3 flex-shrink-0">
          <span class="text-sm font-bold {seeTeamUploads ? 'text-green-600' : 'text-red-600'}">
            {seeTeamUploads ? 'ON' : 'OFF'}
          </span>
          <label class="relative inline-flex items-center {savingSeeTeamUploads ? 'cursor-wait' : 'cursor-pointer'}">
            <input
              type="checkbox"
              checked={seeTeamUploads}
              on:change={(e) => toggleSeeTeamUploads(e.currentTarget.checked)}
              disabled={savingSeeTeamUploads}
              class="sr-only peer"
            />
            <div class="w-14 h-7 bg-red-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
          </label>
        </div>
      </div>
    {/if}

    <!-- Bills List with Tabs -->
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <!-- Tab Navigation -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200">
        <nav class="flex overflow-x-auto" aria-label="Tabs">
          <button
            on:click={() => { activeTab = 'ready'; selectedBillIds = new Set(); }}
            class="px-5 py-3 text-sm font-medium transition-colors rounded-tl-lg
              {activeTab === 'ready' ? 'bg-blue-600 text-white' : 'text-gray-500 border-r border-gray-200 hover:text-gray-700 hover:bg-gray-50'}"
          >
            Ready to Check ({readyBills.length})
          </button>
          <button
            on:click={() => { activeTab = 'synced'; selectedBillIds = new Set(); }}
            class="px-5 py-3 text-sm font-medium transition-colors
              {activeTab === 'synced' ? 'bg-blue-600 text-white' : 'text-gray-500 border-r border-gray-200 hover:text-gray-700 hover:bg-gray-50'}"
          >
            Sync Complete ({syncedBills.length})
          </button>
        </nav>
        <div class="flex flex-col sm:flex-row gap-2 p-3 sm:px-4 sm:py-0">
            <button
              on:click={() => fetchBills()}
              class="hidden sm:inline-flex text-gray-500 hover:text-gray-700 px-2 py-2 rounded hover:bg-gray-100 transition-colors"
              title="Refresh list"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            </button>
            {#if activeTab !== 'synced'}
              <button
                on:click={confirmDeleteSelected}
                disabled={!canDeleteSelected || deleting}
                class="w-full sm:w-auto px-4 py-2.5 min-h-[44px] rounded bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : `Delete Selected (${selectedBillIds.size})`}
              </button>
              <button
                on:click={processSelected}
                disabled={!canProcessSelected}
                class="w-full sm:w-auto px-4 py-2.5 min-h-[44px] rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Review & Sync ({selectedBillIds.size})
              </button>
            {/if}
          </div>
        </div>

      {#if activeBills.length === 0}
        <div class="p-8 text-center text-gray-500">
          {#if activeTab === 'ready'}
            <p>No bills ready to check. Upload PDF invoices above to get started.</p>
          {:else}
            <p>No synced bills yet.</p>
          {/if}
        </div>
      {:else}
        <!-- Mobile card list (compact, no horizontal scrolling) -->
        <div class="sm:hidden divide-y divide-gray-100">
          {#if activeTab !== 'synced' && selectableBills.length > 0}
            <label class="px-3 py-2 flex items-center gap-3 text-sm text-gray-600 bg-gray-50">
              <input
                type="checkbox"
                class="form-checkbox h-5 w-5 text-blue-600"
                checked={allSelectableSelected}
                indeterminate={someSelectableSelected && !allSelectableSelected}
                on:change={toggleSelectAll}
              />
              Select all ({selectableBills.length})
            </label>
          {/if}
          {#each activeBills as bill (bill.uploadId)}
            <div
              class="p-3 flex gap-3 {activeTab === 'synced' ? 'opacity-70 bg-gray-50' : ''} active:bg-[#f0f4ff]"
              role="button"
              tabindex="0"
              on:click={() => openBill(bill)}
              on:keydown={(e) => { if (e.key === 'Enter') openBill(bill); }}
            >
              {#if activeTab !== 'synced'}
                <div class="pt-0.5" on:click|stopPropagation on:keydown|stopPropagation role="presentation">
                  <input
                    type="checkbox"
                    class="form-checkbox h-5 w-5 text-blue-600"
                    checked={selectedBillIds.has(bill.uploadId)}
                    disabled={bill.status === 'processing' || bill.status === 'uploaded'}
                    on:change={() => toggleBillSelection(bill.uploadId)}
                  />
                </div>
              {/if}
              <div class="min-w-0 flex-1">
                <div class="flex items-start justify-between gap-2">
                  <p class="text-sm font-medium text-gray-900 truncate">{bill.extractedData?.vendorName || bill.fileName}</p>
                  <span class="flex-shrink-0 px-2 inline-flex text-[11px] leading-5 font-semibold rounded-full {getStatusClass(bill)}">{getStatusText(bill)}</span>
                </div>
                <p class="text-xs text-gray-400 truncate">{bill.fileName}</p>
                {#if bill.possibleDuplicates?.length}
                  <span class="inline-block mt-0.5 px-1.5 py-0.5 text-[11px] font-semibold rounded bg-amber-100 text-amber-800" title={bill.possibleDuplicates.map(d => (d.reason === 'same-file' ? 'Same file as ' : 'Same supplier and invoice number as ') + (d.fileName || d.uploadId) + ' (' + d.status + ')').join('\n')}>Possible duplicate</span>
                {/if}
                {#if uploaderLabel(bill)}
                  <p class="text-[11px] text-gray-500 mt-0.5">Uploaded by {uploaderLabel(bill)}</p>
                {/if}
                {#if isEmailedToOwner(bill)}
                  <p class="text-[11px] text-purple-600 mt-0.5">Emailed to owner</p>
                {/if}
                <div class="mt-1 flex items-center justify-between gap-2">
                  <span class="text-xs text-gray-500">
                    {bill.extractedData?.issueDate ? formatDate(bill.extractedData?.issueDate) : ''}
                    {#if listDueDate(bill, directBillDueTerms, activeTab).dueDate}<span class="text-gray-400"> · due {formatDate(listDueDate(bill, directBillDueTerms, activeTab).dueDate)}{listDueDate(bill, directBillDueTerms, activeTab).defaulted ? '*' : ''}</span>{/if}
                  </span>
                  <span class="text-sm font-semibold text-gray-900">{formatCurrency(bill.extractedData?.total)}</span>
                </div>
                {#if bill.status === 'failed'}
                  <p class="text-xs text-red-500 mt-1">{bill.errorMessage || 'Extraction failed'}</p>
                {/if}
                {#if activeTab === 'synced'}
                  <div class="mt-1.5 flex items-center gap-3 text-xs text-gray-500">
                    {#if !isQuickBooks}<span>Xero {bill.syncedToXero ? '✓' : '—'}</span>{/if}
                    <span title={isOverheadXeroOnly(bill) ? 'Skipped (overhead)' : ''}>JT {bill.syncedToJT ? '✓' : isOverheadXeroOnly(bill) ? 'n/a' : '—'}</span>
                    <button
                      on:click|stopPropagation={() => navigate('/review-direct-bills', { state: { selectedBills: [bill], readOnly: true } })}
                      class="ml-auto text-blue-500 font-medium"
                    >View</button>
                  </div>
                {:else}
                  <div class="mt-1.5 flex justify-end">
                    <button
                      on:click|stopPropagation={() => confirmDeleteBill(bill.uploadId)}
                      class="text-red-400 hover:text-red-600 text-xs inline-flex items-center gap-1"
                    >
                      <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </div>
                {/if}
              </div>
            </div>
          {/each}
        </div>

        <!-- Desktop table -->
        <div class="hidden sm:block min-w-0 overflow-x-auto">
          <table class="divide-y divide-[#edf0f7]" style="min-width: max(100%, max-content)">
            <thead class="bg-white">
              <tr>
                {#if activeTab !== 'synced'}
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <label class="inline-flex items-center gap-2 cursor-pointer" title="Select all">
                      <input
                        type="checkbox"
                        class="form-checkbox h-4 w-4 text-blue-600"
                        checked={allSelectableSelected}
                        indeterminate={someSelectableSelected && !allSelectableSelected}
                        disabled={selectableBills.length === 0}
                        on:change={toggleSelectAll}
                      />
                      All
                    </label>
                  </th>
                {/if}
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Filename
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Vendor
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Issue Date
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Due Date
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Total
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                {#if activeTab === 'synced'}
                  {#if !isQuickBooks}
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Synced to Xero
                    </th>
                  {/if}
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Synced to JT
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                {:else}
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                {/if}
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-[#edf0f7]">
              {#each activeBills as bill (bill.uploadId)}
                <tr
                  class="hover:bg-[#f0f4ff] cursor-pointer {activeTab === 'synced' ? 'opacity-60 bg-gray-50' : ''}"
                  on:click={() => openBill(bill)}
                >
                  {#if activeTab !== 'synced'}
                    <td class="px-4 py-4 whitespace-nowrap" on:click|stopPropagation>
                      <input
                        type="checkbox"
                        class="form-checkbox h-4 w-4 text-blue-600"
                        checked={selectedBillIds.has(bill.uploadId)}
                        disabled={bill.status === 'processing' || bill.status === 'uploaded'}
                        on:change={() => toggleBillSelection(bill.uploadId)}
                      />
                    </td>
                  {/if}
                  <td class="px-4 py-4 whitespace-nowrap text-sm font-medium {activeTab === 'synced' ? 'text-gray-600' : 'text-gray-900'}">
                    {bill.fileName}
                    {#if bill.possibleDuplicates?.length}
                      <div><span class="inline-block mt-0.5 px-1.5 py-0.5 text-[11px] font-semibold rounded bg-amber-100 text-amber-800" title={bill.possibleDuplicates.map(d => (d.reason === 'same-file' ? 'Same file as ' : 'Same supplier and invoice number as ') + (d.fileName || d.uploadId) + ' (' + d.status + ')').join('\n')}>Possible duplicate</span></div>
                    {/if}
                    {#if uploaderLabel(bill)}
                      <p class="text-[11px] font-normal text-gray-500">Uploaded by {uploaderLabel(bill)}</p>
                    {/if}
                    {#if isEmailedToOwner(bill)}
                      <p class="text-[11px] font-normal text-purple-600">Emailed to owner</p>
                    {/if}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm {activeTab === 'synced' ? 'text-gray-600' : 'text-gray-900'}">
                    {bill.extractedData?.vendorName || 'Extracting...'}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(bill.extractedData?.issueDate)}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {#if listDueDate(bill, directBillDueTerms, activeTab).defaulted}
                      <span class="italic text-gray-400" title="Default terms: {describeDueTerms(directBillDueTerms)}">{formatDate(listDueDate(bill, directBillDueTerms, activeTab).dueDate)}*</span>
                    {:else}
                      {formatDate(bill.extractedData?.dueDate)}
                    {/if}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm {activeTab === 'synced' ? 'text-gray-600' : 'text-gray-900'}">
                    {formatCurrency(bill.extractedData?.total)}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap">
                    <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full {getStatusClass(bill)}" title={bill.status === 'failed' ? (bill.errorMessage || 'Extraction failed') : ''}>
                      {getStatusText(bill)}
                    </span>
                    {#if bill.status === 'failed'}
                      <p class="text-xs text-red-500 mt-1 max-w-[200px] truncate" title={bill.errorMessage || 'Extraction failed'}>{bill.errorMessage || 'Extraction failed'}</p>
                    {/if}
                  </td>
                  {#if activeTab === 'synced'}
                    {#if !isQuickBooks}
                      <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {#if bill.syncedToXero}
                          <span class="text-green-600" title={bill.syncedToXeroAt ? `Synced ${formatDate(bill.syncedToXeroAt)}` : ''}>&#10003;</span>
                        {:else}
                          <span class="text-gray-300">&mdash;</span>
                        {/if}
                      </td>
                    {/if}
                    <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {#if bill.syncedToJT}
                        <span class="text-green-600" title={bill.syncedToJTAt ? `Synced ${formatDate(bill.syncedToJTAt)}` : ''}>&#10003;</span>
                      {:else if isOverheadXeroOnly(bill)}
                        <span class="text-gray-400" title="Skipped (overhead)">n/a</span>
                      {:else}
                        <span class="text-gray-300">&mdash;</span>
                      {/if}
                    </td>
                    <td class="px-4 py-4 whitespace-nowrap text-sm" on:click|stopPropagation>
                      <button
                        on:click={() => navigate('/review-direct-bills', { state: { selectedBills: [bill], readOnly: true } })}
                        class="text-blue-500 hover:text-blue-700 text-xs font-medium transition-colors"
                      >
                        View
                      </button>
                    </td>
                  {:else}
                    <td class="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        on:click|stopPropagation={() => confirmDeleteBill(bill.uploadId)}
                        class="text-red-400 hover:text-red-600 transition-colors"
                        title="Delete bill"
                      >
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  {/if}
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
    <!-- Delete Confirmation Modal -->
    {#if showDeleteConfirm}
      <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">Confirm Delete</h3>
          <p class="text-sm text-gray-600 mb-4">
            Are you sure you want to delete {deleteTargetIds.length === 1 ? 'this bill' : `these ${deleteTargetIds.length} bills`}? This will remove the PDF and all extracted data. This action cannot be undone.
          </p>
          <div class="flex justify-end gap-3">
            <button
              on:click={() => { showDeleteConfirm = false; deleteTargetIds = []; }}
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              on:click={executeDelete}
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    {/if}
  </div>
</Layout>

<style>
  /* Make table responsive */
  @media (max-width: 1200px) {
    td {
      font-size: 0.75rem;
      padding: 0.5rem;
    }
    
    th {
      font-size: 0.75rem;
      padding: 0.5rem;
    }
  }
</style>

